import { Dispatch, SetStateAction, useContext } from "react";
import React from "react";
import { IconType } from "./common/icons/Icon";
import { IRoute } from "./common/infrastructure/routes/routePaths";
import { IStudioSelectItemModel } from "./common/infrastructure/models/studioSelectItemModel";
import { IUserModel } from "./common/infrastructure/hooks/useAuthentication";

export type AppContextValue = {
  privateRoutes: IRoute[];
  publicRoutes: IRoute[];
  additionalLayoutButtonIconType?: IconType | undefined;
  userStudios: IStudioSelectItemModel[];
  isAuthenticated: boolean;
  selectedStudioId: number;
  currentUser: IUserModel | null;
  isDesktop: boolean;
  isDesktopOrTablet: boolean;
  isTablet: boolean;
  isMobile: boolean;
  selectedStudio: IStudioSelectItemModel | undefined;
  showServicesTypeOptions: boolean;
  showGroupTypeOptions: boolean;
  showGymTypeOptions: boolean;
  showStoreTypeOptions: boolean;
  showSidebar: boolean;
  setShowSidebar: (newValue: boolean) => void;
  setSelectedStudioInStorage: (studioId: number) => void;
  /**
   * Switches studio; for a global admin opening a real studio, only after they confirm
   * it in the access dialog and the access is logged. `onSwitched` runs once it switched.
   */
  requestStudioSwitch: (studioId: number, onSwitched?: () => void) => void;
  /** The studio a global admin is being asked to confirm opening, if any. */
  pendingStudioAccess: IStudioSelectItemModel | undefined;
  /** `reason` — why the admin is opening the studio; required by the dialog and the API. */
  confirmStudioAccess: (reason?: string) => void;
  /** Opens the studio without recording anything (the long-press back door). */
  confirmStudioAccessSilently: () => void;
  cancelStudioAccess: () => void;
  isRecordingStudioAccess: boolean;
  /**
   * A global admin's remembered studio is waiting for the startup access check; the
   * layout holds the page back so none of the studio's data loads before it is answered.
   */
  isStudioAccessCheckPending: boolean;
  resetStudios: (options?: { keepStudioAccessConfirmation?: boolean }) => void;
  logout: () => void;
  additionalLayoutButtonOnClick?: () => void;
  setAdditionalLayoutButtonOnClick: (func: () => void) => void;
  setAdditionalLayoutButtonIconType: Dispatch<
    SetStateAction<IconType | undefined>
  >;
};

export const AppContext = React.createContext<AppContextValue>(
  undefined as unknown as AppContextValue
);

export const useAppContext = (): AppContextValue => {
  const contextValue = useContext(AppContext);

  if (!contextValue) {
    throw new Error(
      "To use 'AppContextProvider' make sure that you wrap your component with <AppContextProvider>{{...}}</AppContextProvider>."
    );
  }

  return contextValue;
};
